import axios, { AxiosError, AxiosHeaders, AxiosResponse } from 'axios'
import config from 'config'
import { getLosVariantBaseUrl } from 'hooks/useLosVersion'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'

import {
    unauthorizedRequestInterceptor,
    unauthorizedResponseInterceptor,
} from './auth/unauthorizedInterceptor'
import { defaultLosJsonHeaders } from './requestHeaders'

const api = axios.create({
    baseURL: `${config.API_BASE_URL}/api/`,
    withCredentials: true,
})

api.interceptors.request.use(unauthorizedRequestInterceptor, (error) =>
    Promise.reject(error),
)

api.interceptors.request.use((request) => {
    const headers = AxiosHeaders.from(request.headers) ?? new AxiosHeaders()

    try {
        Object.entries(defaultLosJsonHeaders()).forEach(([key, value]) => {
            if (value !== undefined) headers.set(key, value)
        })

        request.headers = headers
    } catch (error) {
        OctaneLogger.warn('Failed to set request headers', error as Error)
    }

    return request
})

// Override baseURL for sandbox variant routing.
// Scoped to sandbox only — a persisted cookie cannot reroute traffic in any other environment.
api.interceptors.request.use((request) => {
    if (config.ENVIRONMENT !== 'sandbox') return request

    const variantBase = getLosVariantBaseUrl()

    if (variantBase) {
        request.baseURL = `${variantBase}/api/`
    }

    return request
})

api.interceptors.response.use(
    <T>(response: AxiosResponse<T>): T => response.data,
    (error: AxiosError) => {
        OctaneLogger.axiosError(error.message, error)

        return unauthorizedResponseInterceptor(error)
    },
)

export const axiosApi = {
    get: <T>(url: string, requestConfig?: Parameters<typeof api.get>[1]) =>
        api.get<T>(url, requestConfig) as Promise<T>,
}

export const fetchJson = axiosApi.get
